import { useState } from "react";
import { Check, ChevronsUpDown, Search, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useLearnerSearch, LearnerResult } from "@/hooks/useLearnerSearch";

interface LearnerSearchProps {
  onSelectLearner: (learnerId: string | null) => void;
  selectedLearnerId?: string | null;
}

export function LearnerSearch({ onSelectLearner, selectedLearnerId }: LearnerSearchProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedLearner, setSelectedLearner] = useState<LearnerResult | null>(null);

  const { results, isLoading, isSearching } = useLearnerSearch(searchValue);

  const handleSelect = (learner: LearnerResult) => {
    setSelectedLearner(learner);
    onSelectLearner(learner.id);
    setOpen(false);
    setSearchValue("");
  };

  const handleClear = () => {
    setSelectedLearner(null);
    onSelectLearner(null);
    setSearchValue("");
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-sm font-medium flex items-center gap-2">
        <Search className="h-4 w-4" />
        Search by learner
      </Label>
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-[300px] justify-between"
            >
              {selectedLearner ? (
                <div className="flex items-center gap-2 overflow-hidden">
                  <UserAvatar
                    avatarPath={selectedLearner.avatar_url}
                    fullName={selectedLearner.full_name}
                    className="h-5 w-5"
                    thumbnailSize={48}
                  />
                  <span className="truncate">{selectedLearner.full_name || selectedLearner.email}</span>
                </div>
              ) : (
                <span className="text-muted-foreground">Search learners...</span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search by name or email..."
                value={searchValue}
                onValueChange={setSearchValue}
              />
              <CommandList>
                {(isLoading || isSearching) && searchValue.length >= 2 ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
                  </div>
                ) : searchValue.length < 2 ? (
                  <CommandEmpty>Type at least 2 characters to search</CommandEmpty>
                ) : results.length === 0 ? (
                  <CommandEmpty>No learners found.</CommandEmpty>
                ) : (
                  <CommandGroup>
                    {results.map((learner) => (
                      <CommandItem
                        key={learner.id}
                        value={learner.id}
                        onSelect={() => handleSelect(learner)}
                        className="flex items-center gap-3 py-2"
                      >
                        <UserAvatar
                          avatarPath={learner.avatar_url}
                          fullName={learner.full_name}
                          className="h-8 w-8"
                          thumbnailSize={48}
                        />
                        <div className="flex flex-col overflow-hidden">
                          <span className="font-medium truncate">
                            {learner.full_name || "No name"}
                          </span>
                          <span className="text-xs text-muted-foreground truncate">
                            {learner.email}
                          </span>
                        </div>
                        <Check
                          className={cn(
                            "ml-auto h-4 w-4",
                            selectedLearnerId === learner.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {selectedLearner && (
          <Button variant="ghost" size="sm" onClick={handleClear}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}
